import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, TrendingUp, CalendarClock, MapPin, LogOut, Phone, PhoneCall, FileDown, Save, RotateCcw, SlidersHorizontal, Table2, Newspaper, Plus, Pencil, Trash2, Sparkles, Upload, X, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { API } from "@/lib/api";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";

const SELL_LABELS = {
  asap: "Dès que possible / Au plus vite",
  "3_6_mois": "Dans les 3 à 6 mois",
  plus_6_mois: "Dans plus de 6 mois",
  curiosite: "Simple curiosité / Projets futurs",
  "6_mois": "Dans 6 mois",
  non: "Ne souhaite pas vendre",
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("leads");
  const [cityFilter, setCityFilter] = useState("");

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const euro = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";
  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const cities = [...new Set(leads.map((l) => (l.property.city || "").trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "fr")
  );
  const filteredLeads = cityFilter
    ? leads.filter((l) => (l.property.city || "").trim().toLowerCase() === cityFilter.toLowerCase())
    : leads;

  const cards = stats
    ? [
        { icon: Users, label: "Demandes totales", value: stats.total_leads },
        { icon: TrendingUp, label: "Estimation moyenne", value: euro(stats.avg_estimate) },
        { icon: CalendarClock, label: "7 derniers jours", value: stats.last_7_days },
        { icon: MapPin, label: "Ville n°1", value: stats.top_cities?.[0]?.city || "—" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-9 w-9 object-contain" />
            <span className="font-display font-extrabold text-slate-900">Tableau de bord</span>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 font-medium">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {cards.map((c, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={`stat-card-${i}`}>
              <c.icon className="w-6 h-6 text-[#2563EB] mb-4" strokeWidth={1.5} />
              <div className="font-display text-2xl font-extrabold text-slate-900">{c.value}</div>
              <div className="text-sm text-slate-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "leads" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            <Table2 className="w-4 h-4" /> Demandes
          </button>
          <button onClick={() => setTab("prices")} data-testid="tab-prices" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "prices" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            <SlidersHorizontal className="w-4 h-4" /> Prix au m²
          </button>
          <button onClick={() => setTab("articles")} data-testid="tab-articles" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "articles" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            <Newspaper className="w-4 h-4" /> Articles
          </button>
          <button onClick={() => setTab("projets")} data-testid="tab-projets" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "projets" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            <Sparkles className="w-4 h-4" /> Demandes de projet
          </button>
        </div>

        {tab === "prices" && <PriceEditor />}
        {tab === "articles" && <ArticleManager />}
        {tab === "projets" && <ProjectLeads />}

        {tab === "leads" && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display font-bold text-slate-900">Demandes reçues ({filteredLeads.length})</h2>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400" />
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                data-testid="city-filter-select"
                className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
              >
                <option value="">Tous les secteurs ({leads.length})</option>
                {cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {cityFilter && (
                <button
                  onClick={() => setCityFilter("")}
                  data-testid="city-filter-clear"
                  className="text-xs text-slate-400 hover:text-slate-700 font-medium"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-6 py-3 font-semibold">Contact</th>
                  <th className="px-6 py-3 font-semibold">Bien</th>
                  <th className="px-6 py-3 font-semibold">Ville</th>
                  <th className="px-6 py-3 font-semibold">Estimation</th>
                  <th className="px-6 py-3 font-semibold">Conseiller</th>
                  <th className="px-6 py-3 font-semibold">Rappel</th>
                  <th className="px-6 py-3 font-semibold">Projet vente</th>
                  <th className="px-6 py-3 font-semibold">Propriétaire</th>
                  <th className="px-6 py-3 font-semibold">PDF</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`lead-row-${l.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{l.contact.full_name}</div>
                      <div className="text-slate-500 text-xs">{l.contact.email}</div>
                      <div className="text-slate-500 text-xs">{l.contact.phone}</div>
                    </td>
                    <td className="px-6 py-4 capitalize text-slate-700">
                      {l.property.property_type} · {l.property.surface} m²
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 bg-[#2563EB]/10 text-[#2563EB] px-2.5 py-1 rounded-full text-xs font-bold capitalize" data-testid={`lead-city-${l.id}`}>
                        <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} /> {l.property.city}
                      </span>
                      {l.property.address && (
                        <div className="text-xs text-slate-500 mt-1 max-w-[220px]" data-testid={`lead-address-${l.id}`}>
                          {l.property.address}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">{euro(l.calc.estimate_mid)}</td>
                    <td className="px-6 py-4">
                      {l.contact.wants_advisor ? (
                        <span className="inline-flex items-center gap-1 text-[#2563EB] bg-blue-50 px-2 py-1 rounded-full text-xs font-medium">
                          <Phone className="w-3 h-3" /> Oui
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Non</span>
                      )}
                    </td>
                    <td className="px-6 py-4" data-testid={`lead-unlocked-${l.id}`}>
                      {l.contact.unlocked ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full text-xs font-semibold">
                          <PhoneCall className="w-3 h-3" /> À rappeler
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Non débloqué</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-xs">
                      {SELL_LABELS[l.contact.sell_timeline] || "—"}
                    </td>
                    <td className="px-6 py-4">
                      {l.contact.is_owner === true ? (
                        <span className="text-emerald-600 font-medium text-xs">Oui</span>
                      ) : l.contact.is_owner === false ? (
                        <span className="text-slate-500 text-xs">Non</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <a href={`${API}/valuations/${l.id}/pdf`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#2563EB]" data-testid={`lead-pdf-${l.id}`}>
                        <FileDown className="w-5 h-5" />
                      </a>
                    </td>
                  </tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr><td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                    {cityFilter ? `Aucune demande pour « ${cityFilter} ».` : "Aucune demande pour le moment."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function PriceEditor() {
  const [city, setCity] = useState(null);
  const [dept, setDept] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/prices").then((r) => {
      setCity(r.data.city_prices);
      setDept(r.data.dept_prices);
    });
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/prices", { city_prices: city, dept_prices: dept });
      setCity(data.city_prices);
      setDept(data.dept_prices);
      toast.success("Prix mis à jour. Les nouvelles estimations en tiennent compte.");
    } catch (e) {
      toast.error("Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const { data } = await api.post("/admin/prices/reset");
    setCity(data.city_prices);
    setDept(data.dept_prices);
    toast.success("Grille réinitialisée aux valeurs par défaut.");
  };

  if (!city || !dept) return <div className="text-slate-400 py-10 text-center">Chargement...</div>;

  const label = (k) => k.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-6" data-testid="price-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-xl px-5 py-4">
        <p className="text-sm text-slate-600">
          Ajustez le <b>prix moyen au m²</b> par ville (et par département pour les codes postaux).
          Ces valeurs servent de base aux estimations.
        </p>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="prices-reset-btn" className="flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium">
            <RotateCcw className="w-4 h-4" /> Réinitialiser
          </button>
          <button onClick={save} disabled={saving} data-testid="prices-save-btn" className="flex items-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-semibold">
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-display font-bold text-slate-900 mb-4">Villes (€/m²)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.keys(city).map((k) => (
            <div key={k}>
              <label className="block text-xs font-medium text-slate-500 mb-1">{k === "autre" ? "Autre / inconnu" : label(k)}</label>
              <input
                type="number" value={city[k]}
                onChange={(e) => setCity({ ...city, [k]: e.target.value })}
                data-testid={`price-city-${k}`}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-display font-bold text-slate-900 mb-4">Départements (€/m² — via code postal)</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.keys(dept).map((k) => (
            <div key={k}>
              <label className="block text-xs font-medium text-slate-500 mb-1">Dépt {k}</label>
              <input
                type="number" value={dept[k]}
                onChange={(e) => setDept({ ...dept, [k]: e.target.value })}
                data-testid={`price-dept-${k}`}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const CATEGORIES = ["Conseils", "Marché", "Actualités"];
const emptyArticle = { title: "", category: "Conseils", excerpt: "", content: "", cover_image: null, published: true };

function ArticleManager() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // article object or null

  const load = () => {
    setLoading(true);
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'article "${a.title}" ?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article supprimé.");
      load();
    } catch { toast.error("Échec de la suppression."); }
  };

  const togglePublish = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { ...a, published: !a.published });
      load();
    } catch { toast.error("Échec."); }
  };

  if (editing !== null) {
    return <ArticleEditor initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div className="space-y-5" data-testid="article-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Rédigez et publiez vos articles "Actualités &amp; Conseils Immo".</p>
        <button onClick={() => setEditing(emptyArticle)} data-testid="article-new-btn" className="flex items-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-semibold">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB] mx-auto" /></div>
      ) : articles.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="article-empty">
          Aucun article. Cliquez sur "Nouvel article" pour commencer.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="article-list">
          {articles.map((a) => (
            <div key={a.id} className="flex items-center gap-4 px-5 py-4" data-testid={`article-item-${a.id}`}>
              <div className="w-16 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                {a.cover_image ? <img src={mediaSrc(a.cover_image)} alt="" className="w-full h-full object-cover" /> : <Newspaper className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{a.title}</div>
                <div className="text-xs text-slate-400">{a.category} · {a.published ? "Publié" : "Brouillon"}</div>
              </div>
              <button onClick={() => togglePublish(a)} title={a.published ? "Masquer" : "Publier"} data-testid={`article-toggle-${a.id}`} className="p-2 text-slate-400 hover:text-slate-900">
                {a.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => setEditing(a)} title="Modifier" data-testid={`article-edit-${a.id}`} className="p-2 text-slate-400 hover:text-[#2563EB]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(a)} title="Supprimer" data-testid={`article-delete-${a.id}`} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleEditor({ initial, onDone, onCancel }) {
  const [form, setForm] = useState({ ...emptyArticle, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_image", data.url);
    } catch { toast.error("Échec de l'upload."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const generate = async () => {
    const topic = window.prompt("Sujet de l'article à générer (ex : Comment fixer le bon prix de vente à Grenoble) :", form.title);
    if (!topic) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/articles/generate", { topic, category: form.category });
      setForm((f) => ({ ...f, title: data.title, excerpt: data.excerpt, content: data.content, category: data.category }));
      toast.success("Brouillon généré par l'IA. Relisez et ajustez avant de publier.");
    } catch { toast.error("Échec de la génération IA."); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/articles/${form.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success("Article enregistré.");
      onDone();
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="article-editor">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} data-testid="article-cancel-btn" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 font-medium">
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </button>
        <div className="flex gap-2">
          <button onClick={generate} disabled={generating} data-testid="article-ai-btn" className="flex items-center gap-2 border border-[#C85A40] text-[#C85A40] hover:bg-orange-50 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-semibold">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer avec l'IA
          </button>
          <button onClick={save} disabled={saving} data-testid="article-save-btn" className="flex items-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-semibold">
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Titre *</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="article-title-input" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#2563EB] outline-none" placeholder="Titre de l'article" />
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Catégorie</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="article-category-select" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#2563EB] outline-none">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Statut</label>
            <button onClick={() => set("published", !form.published)} data-testid="article-publish-toggle" className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${form.published ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>
              {form.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />} {form.published ? "Publié (visible)" : "Brouillon (masqué)"}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Résumé / extrait</label>
          <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} data-testid="article-excerpt-input" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#2563EB] outline-none resize-none" placeholder="Court résumé affiché dans la liste des articles." />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Image de couverture</label>
          {form.cover_image ? (
            <div className="relative inline-block">
              <img src={mediaSrc(form.cover_image)} alt="" className="h-40 rounded-xl object-cover" data-testid="article-cover-preview" />
              <button onClick={() => set("cover_image", null)} data-testid="article-cover-remove" className="absolute -top-2 -right-2 w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center hover:bg-red-500"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-slate-300 rounded-xl px-4 py-6 justify-center text-slate-500 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors" data-testid="article-cover-upload">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? "Envoi..." : "Ajouter une image de couverture"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="article-cover-input" />
            </label>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Contenu</label>
          <RichTextEditor value={form.content} onChange={(v) => set("content", v)} />
        </div>
      </div>
    </div>
  );
}


function ProjectLeads() {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/admin/project-leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
  };

  if (leads === null) {
    return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB] mx-auto" /></div>;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="project-leads-panel">
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="font-display font-bold text-slate-900">Demandes de projet sur mesure ({leads.length})</h2>
        <p className="text-xs text-slate-400 mt-0.5">Contacts issus de la rubrique "Votre projet immobilier sur mesure".</p>
      </div>
      {leads.length === 0 ? (
        <div className="px-6 py-12 text-center text-slate-400" data-testid="project-leads-empty">Aucune demande de projet pour le moment.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="project-leads-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-6 py-3 font-semibold">Date</th>
                <th className="px-6 py-3 font-semibold">Nom</th>
                <th className="px-6 py-3 font-semibold">Contact</th>
                <th className="px-6 py-3 font-semibold">Projet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => (
                <tr key={l.id} data-testid={`project-lead-${l.id}`} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap">{l.first_name} {l.last_name}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <a href={`mailto:${l.email}`} className="text-[#2563EB] hover:underline">{l.email}</a>
                      <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" /> {l.phone}</a>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 max-w-md">{l.message || <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
